/* eslint-disable */
/**
 * @fileoverview Dashboard Page Component for Clockwise CPA
 *
 * The main dashboard page that provides users with an overview of their work,
 * recent activities, statistics, and quick access to common functions.
 *
 * Key Features:
 * - Real-time statistics display (hours, revenue, clients, invoices)
 * - Recent time entries and activities
 * - Bookmarked clients for quick access
 * - Quick action buttons for common tasks
 * - Permission-based feature visibility
 * - Responsive design with mobile support
 * - Auto-refreshing current time display
 * - Search functionality integration
 *
 * Data Displayed:
 * - Today's logged hours and weekly totals
 * - Monthly revenue and active client counts
 * - Recent time entries and activities
 * - Pending invoices and completed tasks
 * - User's bookmarked clients
 *
 * Dependencies:
 * - AuthContext for user data and permissions
 * - NotificationContext for error handling
 * - UtilContext for utility functions
 * - React Router for navigation
 * - Feather icons for UI elements
 *
 * @author Clockwise CPA Development Team
 * @version 1.0.0
 * @since 2024-01-01
 */

import React, { memo } from 'react';
import { FiRefreshCw } from 'react-icons/fi';

// Import components
import DashboardHeader from './components/DashboardHeader';
import QuickActions from './components/QuickActions';
import UpcomingTasks from './components/UpcomingTasks';

// Import hooks
import { useDashboardData } from './hooks/useDashboardData';

// Import styles
import '../../styles/Dashboard.css';

/**
 * Dashboard Component
 *
 * Renders the main dashboard page with user statistics, recent activities,
 * and quick action buttons. Provides an overview of user's work and system status.
 *
 * @component
 * @returns {JSX.Element} The dashboard page component
 *
 * @example
 * // Used in routing configuration
 * <Route path="/" element={<Dashboard />} />
 */
const Dashboard = memo(() => {
    // Use custom hook for all data fetching and state management
    const {
        user,
        userLoading,
        loading,
        bookmarksLoading,
        bookmarkedClients,
        currentDateTime,
        permissions,
        fetchUserData,
        trackCustomEvent,
        navigate
    } = useDashboardData();

    // Show loading state while user data is being fetched
    if (userLoading) {
        return (
            <div className="dashboard-container">
                <div className="dashboard-loading">
                    <FiRefreshCw className="spinner" />
                    <h2>Loading dashboard...</h2>
                </div>
            </div>
        );
    }

    // Redirect if no user (shouldn't happen with our auth flow, but safety check)
    if (!user) {
        return null;
    }

    return (
        <div className="dashboard-container">
            <main className="dashboard-main">
                {/* Welcome Header */}
                <DashboardHeader
                    user={user}
                    currentDateTime={currentDateTime}
                />

                {/* Quick Links Section - only show if user has any permissions */}
                <QuickActions permissions={permissions} />

                {/* Bookmarked Clients Section */}
                <UpcomingTasks
                    bookmarkedClients={bookmarkedClients}
                    loading={bookmarksLoading}
                    onRefresh={fetchUserData}
                    onClientClick={(client) => navigate(`/clients/${client.id}`)}
                    trackCustomEvent={trackCustomEvent}
                    canViewClients={permissions.canViewClients}
                />
            </main>
        </div>
    );
});

Dashboard.displayName = 'Dashboard';

export default Dashboard;
